import React, { useEffect, useRef, useState } from 'react';

const ChevronDown = ({ open }: { open: boolean }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
    >
        <polyline points="6 9 12 15 18 9"></polyline>
    </svg>
);

interface NobleProvisionDropdownProps {
    value: number;
    options: number[];
    onChange: (val: string) => void;
}

const withCurrentValue = (options: number[], value: number): number[] => {
    if (!options.includes(value) && value >= 0) {
        return [...options, value].sort((a, b) => b - a);
    }
    return options;
};

export const NobleProvisionDropdown = ({ value, options, onChange }: NobleProvisionDropdownProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const frozenOptionsRef = useRef(options);

    const displayOptions = withCurrentValue(
        isOpen ? frozenOptionsRef.current : options,
        value
    );

    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const handleToggle = () => {
        if (!isOpen) {
            frozenOptionsRef.current = withCurrentValue(options, value);
        }
        setIsOpen((prev) => !prev);
    };

    const handleSelect = (opt: number) => {
        onChange(String(opt));
        setIsOpen(false);
    };

    return (
        <div className="relative w-full" ref={dropdownRef}>
            <button
                type="button"
                onClick={handleToggle}
                className="w-full bg-[#0a0a0c] border border-[#E6C299]/40 text-[#E6C299] text-base font-black rounded-lg px-4 py-3 cursor-pointer focus:outline-none focus:border-[#E6C299] focus:ring-2 focus:ring-[#E6C299]/30 transition-all shadow-inner flex items-center justify-between text-left"
            >
                <span>{value}%</span>
                <ChevronDown open={isOpen} />
            </button>

            {isOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 max-h-60 overflow-y-auto bg-[#0a0a0c] border border-[#E6C299]/40 rounded-lg shadow-2xl z-[200]">
                    {displayOptions.map((opt) => (
                        <button
                            key={opt}
                            type="button"
                            onClick={() => handleSelect(opt)}
                            className={`w-full px-4 py-2.5 text-left text-base font-bold transition-colors border-b border-white/5 last:border-0 ${
                                value === opt
                                    ? 'bg-[#E6C299]/20 text-white'
                                    : 'text-[#E6C299] hover:bg-[#E6C299]/10'
                            }`}
                        >
                            {opt}%
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};
